<template>
  <!-- 
    * activity / activity classification
    * historyType
    * - attachment image
    * - attachment file
    * 1 - created
    * 2 - updated
    * 3 - intention
    * 4 - change state
  -->
  <fts-popper
    strategy-fixed
    trigger="hover"
    :delay-on-mouse-over="300"
    placement="top-end"
    :offset="[-8, -7]"
    content-class="history-content-fab"
    class="fts-history-content"
    :class="[
      {'child': child}, 
      {'history-content--fab': fabDropdown},
      {'history-content--deleting': deleting}
    ]"
    :disabled="
      child || 
      !!(formData && formData.id) || 
      isCommentDeleted
    "
    :before-close="fabDropdownBeforeClose"
    @show="fabDropdown=true"
    @hide="fabDropdown=false"
    ref="historyContent"
  >
    <template #content>
      <div
        v-for="x in reactions"
        :key="`act-${x.id}`"
        class="history-content-fab-action"
        @click="selectReaction(x.emoji)"
      >
        <fts-moji :emoji="x.emoji" :size="14" />
      </div>
      <fts-dropdown-select
        append-to-body
        placement="left-start"
        preventOverflowBoundary="body"
        append-toggle-class="history-content-fab-action"
        content-class="dropdown-menu dropdown-menu-w-xs"
        reference-id="fts-confirmation"
        overlay
        @show="openFabDropdown"
        @content:destroyed="fabDropdownResolve && fabDropdownResolve()"
        v-if="isCommentActionable()"
      >
        <font-awesome-icon slot="toggle" :icon="['fas', 'ellipsis-vertical']" />
        <template #menu="{ select }">
          <div 
            class="dropdown-option" 
            @click="select();onEdit();" 
            v-if="isCommentEditable"
          >
            <div class="dropdown-option-icon">
              <i class="fts-icon-edit" />
            </div>
            <div>Edit comment</div>
          </div>
          <div 
            class="dropdown-option dropdown-option--danger" 
            @click="select();onDelete();" 
            v-if="isCommentDeletable"
          >
            <div class="dropdown-option-icon">
              <i class="fts-icon-trash" />
            </div>
            <div>Delete comment</div>
          </div>
        </template>
      </fts-dropdown-select>
    </template>
    <div slot="reference" class="history-content-wrapper">
      <fts-profile 
        :user-name="data.by"
        :profile-photo-size="26"
        :profile-name="false"
        class="history-content-wrapper__l"
      />
      <div class="history-content-wrapper__r">
        <template v-if="formData && formData.id">
          <fts-comment
            :vector-id="vectorId"
            :comment="formData"
            initial-comment
            :scrollIntoViewOptions="{
              block: 'nearest'
            }"
            :class="{'history-content-comment-editor-overlay': submitting}"
            ref="comment"
          />

          <div class="history-content-comment-editor-action">
            <fts-btn
              sm
              outline
              text-transform-init
              :disabled="submitting"
              @click="onCancelEdit"
            >
              Cancel
            </fts-btn>

            <fts-btn
              sm
              color="primary"
              text-transform-init
              box-shadow-none
              :loading="submitting"
              @click="onSubmit"
            >
              Save
            </fts-btn>
          </div>
        </template>
        <template v-else>
        <div class="history-content-header">
          <div class="history-content-header-l">
            <fts-tippy
              tag="span"
              :content="data.by"
              append-trigger-class="history-content-username"
            >
              {{profileName}}
            </fts-tippy>
            <span v-if="data.actor">({{actorProfileName}})</span>
            <template v-if="typeCommentReply">
              <span class="fts-inline-spacer-1"></span>
              <span class="history-content-date">{{ createdDate }}</span>
            </template>
            <template v-if="activity">
              performed
              <span class="history-content-title">{{ activity }}</span>
            </template>
            <span v-else-if="standardActivityType === 1">created this</span>
            <span v-else-if="standardActivityType === 2">updated this</span>
            <span v-else-if="standardActivityType === 12">managed this</span>
            <span v-else-if="standardActivityType === 4">changed state</span>
            <span v-else-if="standardActivityType === 7">has done assigned</span>
            <span v-else-if="standardActivityType === 8">imported this</span>
            <span v-else-if="standardActivityType === 9">merged this</span>
            <span v-else-if="typeComment">commented this</span>

            <span v-if="data.automationRunId">via automation</span>
          </div>
          <!-- state -->
          <fts-tippy
            :content="data.state.name"
            :options="{ placement: 'left' }"
            class="history-content-state-wrapper"
            v-if="data.state && data.state.name"
          >
            <fts-state
              :state="data.state"
              :states="states"
              class="history-content-state"
            />
          </fts-tippy>
        </div>

        <div class="history-content-body" v-if="data.deleted">
          <div class="history-content-deleted">
            <i class="fts-icon-trash history-content-deleted-icon" />
            <span v-if="data.lastChangeBy">This message was deleted by {{getUserDisplayName(data.lastChangeBy)}}.</span>
            <span v-else>This message was deleted.</span>
          </div>
        </div>

        <div class="history-content-body" v-else-if="Boolean(data.attachments && data.attachments.length) || isHistoryContent">
          <div>
            <!-- attachment -->
            <fts-file
              :prefixURL="`${vectorId}-comment/`"
              :data="data.attachments"
              :downloadable="true"
              :audit="{
                userName: data.by,
                date: data.on,
              }"
              :description="translateContent"
              class="history-content-atch"
            />
            <!-- comment -->
            <div
              class="history-content-comment fts-contenteditable-display"
              v-html="translateContent"
              v-if="isHistoryContent"
            ></div>
          </div>
          <!-- edited -->
          <div class="history-content-edited" v-if="!!data.editedOn">
            Edited
          </div>
        </div>
        <!-- reaction -->
        <div class="history-content-reaction" v-if="!isCommentDeleted && reactionList">
          <div class="history-content-reaction-container">
            <fts-tippy
              v-for="(val, emoji) in reactionList"
              :key="emoji"
              append-box-class="tippy-box-history-reaction"
              append-trigger-class="history-content-reaction-wrapper"
              append-content-class="tippy-content-history-reaction"
              class="history-content-reaction-item"
              :class="{'history-content-reaction-item--active': isReactByCurrentUser(val)}"
              @trigger:click="selectReaction(emoji)"
            >
              <fts-moji :emoji="emoji" :size="14" />
              <div class="history-content-reaction-count">
                {{ val.length }}
              </div>
              <template #content>
                <fts-moji
                  :emoji="emoji"
                  :size="32"
                  class="history-reaction-emoji"
                />
                <div class="history-reaction-user-container">
                  <fts-profile
                    v-for="user in val"
                    :key="user.username"
                    :user-name="user.username"
                    class="history-reaction-user"
                  />
                  <div class="history-reaction-name" v-if="getReactionName(emoji)">
                    reacted with {{getReactionName(emoji)}}
                  </div>
                </div>
              </template>
            </fts-tippy>
          </div>
        </div>
        <!-- footer -->
        <div class="history-content-footer" v-if="!child">
          <!-- date -->
          <div class="history-content-footer-wrapper">
            <span>{{ createdDate }}</span>
            <!-- channel -->
            <span v-if="channel">
              via
              <a
                :href="channel.url"
                target="_blank"
                class="fts-link-primary"
                v-if="channel.url"
              >
                {{ channel.name }}
              </a>
              <span class="fts-text-primary" v-else>
                {{ channel.name }}
              </span>
            </span>
          </div>
          <!-- comment -->
          <div
            class="
              history-content-footer-wrapper history-content-action
              fts-link-default
            "
            @click="reply"
            v-if="
              commentSection && 
              !child && 
              !isCommentDeleted
            "
          >
            reply
          </div>
          <!-- change log -->
          <fts-tippy
            :options="{
              placement: 'left-start',
              interactive: true,
              delay: [500, 300],
              theme: 'light-border'
            }"
            class="history-content-footer-wrapper"
            append-box-class="tippy-box-history-change-log"
            popper-prevent-overflow
            v-if="data.changes && data.changes.length > 0"
          >
            <div
              slot="trigger"
              class="history-content-footer-wrapper history-content-action fts-cursor-default"
            >
              changes
            </div>
            <template #content>
              <div class="change-log-header">
                <div class="change-log-header-title">Changes</div>
                <div class="change-log-header-date">{{ createdDate }}</div>
              </div>
              <div class="change-log-content fts-scrollbar">
                <fts-history-log :module-id="vectorId" :log="data.changes" />
              </div>
            </template>
          </fts-tippy>
          <!--trace-->
          <div v-if="isAlpha" class="history-content-footer-wrapper fts-link-default"><a @click="trace()">trace</a> <fts-release-badge alpha class="fts-align-top" /></div>
        </div>
        </template>
      </div>
      <!--trace modal-->
      <trace-modal
        append-to-body
        :requestId="data.requestId"
        :historyId="data.id"
        ref="traceModal"
      />
    </div>
  </fts-popper>
</template>

<script>
import moment from 'moment';

import { authorizationStore } from '../../store/authorizationStore';
import { classificationStore } from '../../store/classificationStore';
import { clientStore } from '../../store/clientStore';
import commons from '../../commons/commons';
import user from '../../commons/user';
import { htmlValidator, commentMentionsValidator } from '../../commons/validator';

import FTSBtn from '../FTSBtn';
import FTSComment from '../FTSComment';
import FTSDropdownSelect from '../FTSDropdownSelect';
import FTSFile from '../file/FTSFile';
import FTSHistoryLog from './FTSHistoryLog';
import FTSMoji from '../FTSMoji';
import FTSPopper from '../FTSPopperNew';
import FTSProfile from '../FTSProfile';
import FTSState from '../FTSState';
import FTSTippy from '../FTSTippy';
import TraceModal from './TraceModal.vue';

export default {
  props: {
    data: Object,
    child: Boolean,
    locusId: [String, Number],
    vectorId: [String, Number],
    information: Array,
    states: Array,
    users: Array,
    commentSection: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    "trace-modal": TraceModal,
    "fts-btn": FTSBtn,
    "fts-comment": FTSComment,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-file": FTSFile,
    "fts-history-log": FTSHistoryLog,
    "fts-moji": FTSMoji,
    "fts-profile": FTSProfile,
    "fts-popper": FTSPopper,
    "fts-state": FTSState,
    "fts-tippy": FTSTippy
  },
  data: () => ({
    reactions: [
      { id: "thumbsup", emoji: "👍", name: ":thumbsup:" },
      { id: "celebration", emoji: "🎉", name: ":tada:" },
      { id: "biceps", emoji: "💪", name: ":muscle:" },
      { id: "heart", emoji: "❤", name: ":heart:" },
      { id: "raised", emoji: "🙌", name: ":raised_hands:" },
      { id: "joy", emoji: "😂", name: ":joy:" },
    ],
    formData: null,
    fabDropdown: false,
    fabDropdownPromise: null,
    fabDropdownResolve: null,
    isCommentEditable: false,
    isCommentDeletable: false,
    submitting: false,
    deleting: false
  }),
  methods: {
    moment,
    selectReaction(emoji) {
      const userName = authorizationStore.state.userName;
      let reaction = this.data.reactions && this.data.reactions.find(
        (x) => x.emoji === emoji && x.username === userName
      );
      if (reaction) {
        var ind = this.data.reactions.indexOf(reaction);
        this.data.reactions.splice(ind, 1);
      } else {
        if (!this.data.reactions) {
          this.$set(this.data, "reactions", []);
        }
        this.data.reactions.push({
          emoji: emoji,
          username: userName,
        });
      }

      classificationStore.dispatch("reactActivity", {
        emoji: emoji,
        history: this.data.history,
        locusDetailId: this.data.history ? this.data.id : null,
        locusCollaborationId: this.data.history ? null : this.data.id,
        id: this.data.entryId,
      });
    },
    getReactionName(emoji) {
      const reaction = this.reactions.find(x => x.emoji === emoji);
      return reaction && reaction.name;
    },
    reply() {
      this.$emit(
        "comment:reply",
        Boolean(this.data && this.data.by) && this.data.by !== this.currentUsername ?
          `<p><span class="mention" data-mention-id="${this.data.by}">@${this.data.by}</span> </p>` :
          '<p></p>'
      );
    },
    onEdit() {
      this.$refs.historyContent.showPopper = false;
      this.formData = {...this.data};
      if (this.formData.content) {
        try {
          const matches = commentMentionsValidator(this.formData.content);
          if (matches) {
            const mentions = [...new Set (matches)];
            if (mentions.length) {
              for (let i = 0; i < mentions.length; i++) {
                this.formData.content = this.formData.content.replaceAll(
                  mentions[i],
                  `<span class="mention" data-mention-id="${mentions[i].substring(1)}">${mentions[i]}</span>`
                );
              }
            }
          }
        } catch(err) {
          console.error(err);
        }
        this.formData.content = this.formData.content.replaceAll('&nbsp;', ' ');
        this.formData.content = this.formData.content.replaceAll(/\r/g, '');
        const convertToParagraphs = (text) => {
          // remove empty lines between paragraphs
          let result = text.replace(/(<p>.*?<\/p>)|\s*$/gs, (match, pTag) => pTag || '');
          // wrap lines without <p> tags with <p> tags
          result = result.replace(/^(?!<p>).*$/gm, '<p>$&</p>');
          return result;
        }
        this.formData.content = convertToParagraphs(this.formData.content);
      }
      this.$nextTick(() => {
        this.$refs.comment.onFocus();
      });
    },
    onCancelEdit() {
      this.formData = null;
    },
    async onSubmit() {
      try {
        this.submitting = true;
        const submitData = {...this.formData};
        const res = await classificationStore.dispatch("sendComment", {
          id: this.data.id,
          attachments: submitData.attachments,
          content: submitData.content,
          locusId: this.data.entryId || this.locusId,
          type: !!this.data.comment ? 1 : 0
        });

        this.$emit('update:data', {...this.data, editedOn: moment().format()});
        this.formData = null;
      } catch(err) {
        console.error(err);
        this.$toasted.global.error({
          response: err.response,
          message: 'Failed to update comment'
        });
      } finally {
        this.submitting = false;
      }
    },
    async onDelete() {
      this.deleting = true;
      const res = await this.$confirmation.show({
        type: "delete",
        title: "comment",
        text: "comment"
      });
      if (res === true) {
        try {
          const res = await classificationStore.dispatch("sendComment", {
            id: this.data.id,
            attachments: this.data.attachments,
            content: this.data.content,
            locusId: this.data.entryId || this.locusId,
            type: !!this.data.comment ? 1 : 0,
            delete: true
          });

          this.$emit('update:data', {...this.data, deleted: true});
        } catch(err) {
          console.error(err);
          this.$toasted.global.error({
            response: err.response,
            message: 'Failed to delete comment'
          });
        }
      }
      this.deleting = false;
    },
    openFabDropdown() {
      this.fabDropdownPromise = new Promise(resolve => {
        this.fabDropdownResolve = resolve
      });
    },
    async fabDropdownBeforeClose() {
      await this.fabDropdownPromise;
      await new Promise(resolve => setTimeout(resolve));
    },
    getUserDisplayName(username) {
      return user.displayName(username, this.users);
    },
    isReactByCurrentUser(list) {
      return list.some(x => x.username == this.currentUsername);
    },
    isCommentActionable() {
      if (!authorizationStore.getters.isDevelopment)
        return false;

      if (!this.data || this.data.deleted === true)
        return false;
      
      const isWithinTimeLimit = (unit, limit) => moment().diff(this.data.on, unit) <= limit;

      const isComment = () => this.data.content || (this.data.attachments && this.data.attachments.length);

      const isCommentEditable = () => (
        isWithinTimeLimit("minutes", 15) && 
        isComment() && 
        this.data.by === this.currentUsername
      );

      const isCommentDeletable = () => (
        isWithinTimeLimit("hours", 60) && 
        isComment() && 
        (
          this.data.by === this.currentUsername || authorizationStore.getters.isAdminUser
        )
      );

      this.isCommentEditable = isCommentEditable();
      this.isCommentDeletable = isCommentDeletable();

      return this.isCommentEditable || this.isCommentDeletable;
    },
    trace() {
      //alert(this.data.requestId);
      this.$refs.traceModal.show();
    },
  },
  computed: {
    isAlpha() {
      return authorizationStore.getters.isInternal;
    },
    currentUsername() {
      return authorizationStore.getters.userName;
    },
    profileName() {
      return user.displayName(this.data.by, clientStore.getters.workspaceUsers);
    },
    actorProfileName() {
      return user.displayName(this.data.actor, clientStore.getters.workspaceUsers);
    },
    reactionList() {
      const res = _.groupBy(this.data.reactions, "emoji");
      return !_.isEmpty(res) && res;
    },
    standardActivityType() {
      return this.data.type ? parseInt(this.data.type) : 0;
    },
    activity() {
      return this.data.activity;
    },
    typeActivity() {
      return this.activity || this.standardActivityType ? true : false;
    },
    typeComment() {
      return !this.typeActivity && !this.child;
    },
    typeCommentReply() {
      return !this.typeActivity && this.child;
    },
    file() {
      if (
        this.data.attachment &&
        this.data.attachment.type === "file" &&
        this.data.attachment.path
      ) {
        return this.data.attachment;
      }
    },
    createdDate() {
      return this.data.on ? moment(this.data.on).format('lll') : null;
    },
    isHistoryContent() {
      return htmlValidator(this.data.content);
    },
    translateContent() {
      if (this.isHistoryContent)
        return commons.translateCommentContent(this.data.content, this.users);
    },
    channel() {
      if (this.data && this.data.channel)
        switch (this.data.channel) {
          case "ios":
            return {
              name: "iOS App",
              url: commons.iOSAppStoreURL,
            };
          case "android":
            return {
              name: "Android App",
              url: commons.androindPlayStoreURL,
            };
          case "zapier":
            return {
              name: "Zapier",
            };
          case "integromat":
            return {
              name: "Make",
            };
        }
    },
    isCommentDeleted() {
      return this.data.deleted === true && this.data.comment === true;
    },
  }
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-history-content {
  position: relative;
  margin-left: 0.25rem;
  margin-right: 0.25rem;
  border-radius: 0.5rem;
  // font-size: var(--inistate-paragraph-text-12);
  // line-height: var(--inistate-paragraph-text-12--line-height);
  &:hover,
  &.history-content--fab {
    background-color: var(--fts-gray-100);
  }
  &.history-content--deleting {
    background-color: rgba(var(--fts-danger-rgb), 0.075);
    &::after {
      position: absolute;
      inset: 0;
      content: "";
      cursor: wait;
    }
  }
  &.child {
    margin-top: 0.375rem;
    margin-bottom: 0.375rem;
    margin-left: calc(1.625rem + 0.75rem + 0.5rem);
    margin-right: 0.75rem;
    box-shadow: var(--fts-elevation-1);
    background-color: var(--fts-card-background);
    .history-content-body {
      margin-bottom: 0.25rem;
      padding: 0;
      box-shadow: none;
    }
  }
  .history-content-wrapper {
    display: flex;
    align-items: flex-start;
    padding: 0.625rem 0.5rem;
    .history-content-header {
      display: flex;
      align-items: flex-start;
      .history-content-header-l {
        flex: 1;
        font-size: var(--inistate-paragraph-text-12);
        line-height: var(--inistate-paragraph-text-12--line-height);
        // line-height: 1.5;
        color: var(--fts-default);
        .history-content-header-wrapper {
          white-space: normal;
        }
        .history-content-username {
          color: var(--fts-primary);
        }
        .history-content-title {
          font-weight: 500;
          color: var(--fts-text);
        }
        .history-content-date {
          font-size: 0.675rem;
          color: var(--fts-default);
        }
      }
      .history-content-state-wrapper {
        flex-shrink: 0;
        margin-left: 0.625rem;
        min-width: 0;
        max-width: 35%;
      }
      .history-content-state {
        > div {
          font-size: 0.75rem;
        }
      }
    }
    .history-content-atch {
      .file-wrapper {
        max-width: calc(50% - 0.25rem);
      }
      & + .history-content-comment {
        margin-top: 0.375rem;
      }
    }
    .history-content-comment {
      font-size: var(--inistate-label-text-14);
      line-height: var(--inistate-paragraph-text-14--line-height);
      overflow-wrap: anywhere;
      word-break: break-word;
      white-space: pre-wrap;
    }
    .history-content-comment-editor-action {
      margin-top: 0.5rem;
      text-align: right;
    }
    .history-content-comment-editor-overlay {
      position: relative;
      &::after {
        content: "";
        position: absolute;
        inset: 0;
        cursor: wait;
      }
    }
    .history-content-deleted {
      font-style: italic;
      font-size: var(--inistate-label-text-14);
      line-break: var(--inistate-paragraph-text-14--line-height);
      color: var(--fts-default);
      .history-content-deleted-icon {
        padding-right: 0.125rem;
        font-size: var(--inistate-paragraph-text-16);
        // font-size: 1rem;
        vertical-align: text-top;
      }
    }
    .history-content-edited {
      flex: 1;
      margin: 0.125rem 0 -0.125rem 0.375rem;
      text-align: right;
      color: var(--fts-default);
    }
    .history-content-footer {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      margin-top: 0.25rem;
      .history-content-footer-wrapper {
        &:not(:last-child) {
          margin-right: 0.625rem;
        }
        // font-size: 0.75rem;
        // line-height: 1.5;
        font-size: var(--inistate-paragraph-text-12);
        line-height: var(--inistate-paragraph-text-12--line-height);
        color: var(--fts-default);
      }
    }
  }
  .history-content-wrapper__l {
    margin-right: 0.5rem;
  }
  .history-content-wrapper__r {
    flex: 1;
    min-width: 0;
  }
  .history-content-body {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    padding: 0.5rem;
    box-shadow: var(--fts-elevation-1);
    border-radius: 0.375rem;
    background-color: var(--fts-card-background);
  }
  .history-content-reaction {
    display: flex;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    .history-content-reaction-container {
      @include flex-gap($display: flex, $gap: 0.25rem);
    }
    .history-content-reaction-wrapper {
      display: flex;
      align-items: center;
      padding: 0.125rem 0.375rem;
      border: 1px solid var(--fts-border);
      border-radius: 0.375rem;
      background-color: var(--fts-gray-25);
      color: var(--fts-gray-600);
      user-select: none;
      cursor: pointer;
      .history-content-reaction-count {
        margin-left: 0.25rem;
        font-size: 0.75rem;
      }
    }
    .history-content-reaction-item {
      &.history-content-reaction-item--active {
        .history-content-reaction-wrapper {
          border-color: var(--fts-primary);
          background-color: rgba(var(--fts-primary-rgb), 0.05);
          color: var(--fts-primary);
        }
      }
    }
  }
}
/* history mention */
.history-content-mention {
  display: inline-flex;
  padding: 0.125rem 0.15rem;
  border-radius: 0.25rem;
  color: var(--fts-primary);
  line-height: normal;
}
/* change log */
.tippy-box-history-change-log {
  padding: 0;
  max-height: 400px;
  .change-log-header {
    display: flex;
    align-items: baseline;
    padding: 0.75rem;
    border-bottom: 1px solid var(--fts-border);
    .change-log-header-title {
      font-weight: 500;
      font-size: 0.875rem;
    }
    .change-log-header-date {
      margin-left: 0.5rem;
      font-size: 0.75rem;
    }
  }
  .change-log-content {
    margin-top: 0.25rem;
    margin-bottom: 0.25rem;
    white-space: normal;
    word-break: break-all;
  }
}
/* tippy (reaction) */
.tippy-box-history-reaction {
  padding: 1rem;
  .tippy-content-history-reaction {
    display: flex;
    align-items: center;
    .history-reaction-emoji {
      margin-right: 1.25rem;
    }
    .history-reaction-user-container {
      @include flex-gap($display: flex, $gap: 0.375rem);
      align-items: center;
    }
    .profile-name {
      color: var(--fts-light);
    }
  }
}

.history-content-fab {
  display: flex;
  align-items: center;
  padding: 0.25rem;
  border-radius: 0.25rem;
  user-select: none;
  .history-content-fab-action {
    height: 1.625rem;
    width: 1.625rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.25rem;
    font-size: 0.875rem;
    color: var(--fts-default);
    &:hover {
      background-color: var(--fts-gray-100);
      color: var(--fts-text);
      cursor: pointer;
    }
  }
}
</style>